<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { conversationFilterModeForVisibility } from 'dashboard/helper/agentVisibility';
import {
  CHANNEL_HEALTH_STATUS,
  channelIconForInbox,
  channelHealthForInbox,
  channelLabelKeyForInbox,
} from './helpers/channel';
import EssentialChannelHealth from './EssentialChannelHealth.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const savingAgents = reactive({});
const selectedTeamId = ref('');
const selectedTeamMemberIds = ref([]);
const selectedTeamName = ref('');
const newTeamName = ref('');
const selectedVisibilityAgentId = ref('');
const selectedVisibleTeamIds = ref([]);
const selectedFilterAssignedOnly = ref(false);
const selectedFilterUnassignedOnly = ref(false);
const selectedInboxId = ref('');
const selectedInboxMemberIds = ref([]);
const isFetchingInboxMembers = ref(false);
const isSavingTeam = ref(false);
const isSavingMembers = ref(false);
const isSavingVisibility = ref(false);
const isSavingInboxMembers = ref(false);

const accountId = computed(() => route.params.accountId);
const currentUser = computed(() => store.getters.getCurrentUser || {});
const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const agentUiFlags = computed(() => store.getters['agents/getUIFlags'] || {});
const teamMembersUiFlags = computed(
  () => store.getters['teamMembers/getUIFlags'] || {}
);
const selectedTeam = computed(
  () =>
    teams.value.find(
      team => String(team.id) === String(selectedTeamId.value)
    ) || {}
);
const selectedTeamMembers = computed(() => {
  if (!selectedTeamId.value) return [];
  return store.getters['teamMembers/getTeamMembers'](selectedTeamId.value);
});
const selectedInbox = computed(
  () =>
    inboxes.value.find(
      inbox => String(inbox.id) === String(selectedInboxId.value)
    ) || {}
);
const selectedVisibilityAgent = computed(
  () =>
    agents.value.find(
      agent => String(agent.id) === String(selectedVisibilityAgentId.value)
    ) || {}
);

const currentAccountRole = computed(() =>
  currentAccount.value.role === 'administrator'
    ? t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR')
    : t('AGENT_MGMT.AGENT_TYPES.AGENT')
);

const currentInterfaceMode = computed(() =>
  currentAccount.value.interface_mode === 'essential'
    ? t('AGENT_MGMT.INTERFACE_MODE.ESSENTIAL')
    : t('AGENT_MGMT.INTERFACE_MODE.PRO')
);

const currentUserContext = computed(() =>
  [
    currentUser.value.name || currentUser.value.email,
    currentAccountRole.value,
    currentInterfaceMode.value,
  ]
    .filter(Boolean)
    .join(' - ')
);

const stats = computed(() => {
  const essentialAgents = agents.value.filter(
    agent => agent.interface_mode === 'essential'
  ).length;
  const channelAlerts = inboxes.value.filter(inbox =>
    [
      CHANNEL_HEALTH_STATUS.ATTENTION,
      CHANNEL_HEALTH_STATUS.WARNING,
      CHANNEL_HEALTH_STATUS.UNKNOWN,
    ].includes(channelHealthForInbox(inbox).status)
  ).length;

  return [
    {
      id: 'essential_agents',
      icon: 'i-lucide-monitor-smartphone',
      label: t('ESSENTIAL.ADMIN.STATS.ESSENTIAL_AGENTS'),
      value: essentialAgents,
    },
    {
      id: 'agents',
      icon: 'i-lucide-users-round',
      label: t('ESSENTIAL.ADMIN.STATS.AGENTS'),
      value: agents.value.length,
    },
    {
      id: 'teams',
      icon: 'i-lucide-network',
      label: t('ESSENTIAL.ADMIN.STATS.TEAMS'),
      value: teams.value.length,
    },
    {
      id: 'channel_alerts',
      icon: 'i-lucide-activity',
      label: t('ESSENTIAL.ADMIN.STATS.CHANNEL_ALERTS'),
      value: channelAlerts,
    },
  ];
});

const interfaceModes = computed(() => [
  {
    id: 'pro',
    label: t('AGENT_MGMT.INTERFACE_MODE.PRO'),
  },
  {
    id: 'essential',
    label: t('AGENT_MGMT.INTERFACE_MODE.ESSENTIAL'),
  },
]);
const channelLabels = computed(() => ({
  'ESSENTIAL.CHANNELS.WHATSAPP': t('ESSENTIAL.CHANNELS.WHATSAPP'),
  'ESSENTIAL.CHANNELS.WHATSAPP_EVOLUTION': t(
    'ESSENTIAL.CHANNELS.WHATSAPP_EVOLUTION'
  ),
  'ESSENTIAL.CHANNELS.WHATSAPP_API': t('ESSENTIAL.CHANNELS.WHATSAPP_API'),
  'ESSENTIAL.CHANNELS.TELEGRAM': t('ESSENTIAL.CHANNELS.TELEGRAM'),
  'ESSENTIAL.CHANNELS.EMAIL': t('ESSENTIAL.CHANNELS.EMAIL'),
  'ESSENTIAL.CHANNELS.SITE': t('ESSENTIAL.CHANNELS.SITE'),
  'ESSENTIAL.CHANNELS.OTHER': t('ESSENTIAL.CHANNELS.OTHER'),
}));

const proUrl = path => frontendURL(`accounts/${accountId.value}/${path}`);
const navigateTo = path => router.push(proUrl(path));
const isAgentSelectedForTeam = agentId =>
  selectedTeamMemberIds.value.includes(agentId);
const isAgentSelectedForInbox = agentId =>
  selectedInboxMemberIds.value.includes(agentId);
const isTeamVisibleForAgent = teamId =>
  selectedVisibleTeamIds.value.includes(teamId);
const channelLabel = inbox =>
  channelLabels.value[channelLabelKeyForInbox(inbox)] ||
  t('ESSENTIAL.CHANNELS.OTHER');

const buildConversationFilterMode = () =>
  conversationFilterModeForVisibility({
    visibleTeamIds: selectedVisibleTeamIds.value,
    filterAssignedOnly: selectedFilterAssignedOnly.value,
    filterUnassignedOnly: selectedFilterUnassignedOnly.value,
  });

const updateInterfaceMode = async (agent, interfaceMode) => {
  if (agent.interface_mode === interfaceMode) return;

  savingAgents[agent.id] = true;
  try {
    await store.dispatch('agents/update', {
      id: agent.id,
      interface_mode: interfaceMode,
    });
    useAlert(t('ESSENTIAL.ADMIN.AGENTS.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('ESSENTIAL.ADMIN.AGENTS.UPDATE_ERROR'));
  } finally {
    savingAgents[agent.id] = false;
  }
};

const selectTeam = teamId => {
  selectedTeamId.value = String(teamId || '');
};

const syncSelectedTeamForm = () => {
  selectedTeamName.value = selectedTeam.value?.name || '';
  selectedTeamMemberIds.value = selectedTeamMembers.value.map(
    agent => agent.id
  );
};

const fetchSelectedTeamMembers = async teamId => {
  if (!teamId) {
    selectedTeamMemberIds.value = [];
    return;
  }

  try {
    await store.dispatch('teamMembers/get', { teamId });
  } catch (error) {
    selectedTeamMemberIds.value = [];
  }
};

const createTeam = async () => {
  const name = newTeamName.value.trim();
  if (!name) {
    useAlert(t('ESSENTIAL.ADMIN.TEAMS.NAME_REQUIRED'));
    return;
  }

  isSavingTeam.value = true;
  try {
    const team = await store.dispatch('teams/create', {
      name,
      description: '',
      allow_auto_assign: true,
    });
    newTeamName.value = '';
    selectTeam(team.id);
    await fetchSelectedTeamMembers(team.id);
    useAlert(t('ESSENTIAL.ADMIN.TEAMS.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('ESSENTIAL.ADMIN.TEAMS.CREATE_ERROR'));
  } finally {
    isSavingTeam.value = false;
  }
};

const updateTeamName = async () => {
  if (!selectedTeamId.value) return;

  const name = selectedTeamName.value.trim();
  if (!name) {
    useAlert(t('ESSENTIAL.ADMIN.TEAMS.NAME_REQUIRED'));
    return;
  }

  isSavingTeam.value = true;
  try {
    await store.dispatch('teams/update', {
      id: selectedTeamId.value,
      name,
      description: selectedTeam.value.description || '',
      allow_auto_assign: selectedTeam.value.allow_auto_assign ?? true,
    });
    useAlert(t('ESSENTIAL.ADMIN.TEAMS.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('ESSENTIAL.ADMIN.TEAMS.UPDATE_ERROR'));
  } finally {
    isSavingTeam.value = false;
  }
};

const toggleTeamMember = agentId => {
  if (isAgentSelectedForTeam(agentId)) {
    selectedTeamMemberIds.value = selectedTeamMemberIds.value.filter(
      id => id !== agentId
    );
    return;
  }

  selectedTeamMemberIds.value = [...selectedTeamMemberIds.value, agentId];
};

const saveTeamMembers = async () => {
  if (!selectedTeamId.value) return;

  isSavingMembers.value = true;
  try {
    await store.dispatch('teamMembers/update', {
      teamId: selectedTeamId.value,
      agentsList: selectedTeamMemberIds.value,
    });
    await store.dispatch('teams/get');
    useAlert(t('ESSENTIAL.ADMIN.TEAMS.MEMBERS_SUCCESS'));
  } catch (error) {
    useAlert(t('ESSENTIAL.ADMIN.TEAMS.MEMBERS_ERROR'));
  } finally {
    isSavingMembers.value = false;
  }
};

const selectVisibilityAgent = agentId => {
  selectedVisibilityAgentId.value = String(agentId || '');
};

const syncVisibilityForm = () => {
  selectedVisibleTeamIds.value = [
    ...(selectedVisibilityAgent.value?.visible_team_ids || []),
  ];
  selectedFilterAssignedOnly.value = Boolean(
    selectedVisibilityAgent.value?.filter_assigned_only
  );
  selectedFilterUnassignedOnly.value = Boolean(
    selectedVisibilityAgent.value?.filter_unassigned_only
  );
};

const showAllTeamsForAgent = () => {
  selectedVisibleTeamIds.value = [];
};

const toggleVisibleTeam = teamId => {
  if (isTeamVisibleForAgent(teamId)) {
    selectedVisibleTeamIds.value = selectedVisibleTeamIds.value.filter(
      id => id !== teamId
    );
    return;
  }

  selectedVisibleTeamIds.value = [...selectedVisibleTeamIds.value, teamId];
};

const saveAgentVisibility = async () => {
  if (!selectedVisibilityAgentId.value) return;

  isSavingVisibility.value = true;
  try {
    await store.dispatch('agents/update', {
      id: selectedVisibilityAgentId.value,
      conversation_filter_mode: buildConversationFilterMode(),
      visible_team_ids: selectedVisibleTeamIds.value,
      filter_assigned_only: selectedFilterAssignedOnly.value,
      filter_unassigned_only: selectedFilterUnassignedOnly.value,
    });
    useAlert(t('ESSENTIAL.ADMIN.AGENTS.VISIBILITY_SUCCESS'));
  } catch (error) {
    useAlert(t('ESSENTIAL.ADMIN.AGENTS.VISIBILITY_ERROR'));
  } finally {
    isSavingVisibility.value = false;
  }
};

const selectInbox = inboxId => {
  selectedInboxId.value = String(inboxId || '');
};

const fetchSelectedInboxMembers = async inboxId => {
  if (!inboxId) {
    selectedInboxMemberIds.value = [];
    return;
  }

  isFetchingInboxMembers.value = true;
  try {
    const response = await store.dispatch('inboxMembers/get', { inboxId });
    const inboxMembers = response.data?.payload || [];
    selectedInboxMemberIds.value = inboxMembers.map(agent => agent.id);
  } catch (error) {
    selectedInboxMemberIds.value = [];
  } finally {
    isFetchingInboxMembers.value = false;
  }
};

const toggleInboxMember = agentId => {
  if (isAgentSelectedForInbox(agentId)) {
    selectedInboxMemberIds.value = selectedInboxMemberIds.value.filter(
      id => id !== agentId
    );
    return;
  }

  selectedInboxMemberIds.value = [...selectedInboxMemberIds.value, agentId];
};

const saveInboxMembers = async () => {
  if (!selectedInboxId.value) return;

  isSavingInboxMembers.value = true;
  try {
    await store.dispatch('inboxMembers/create', {
      inboxId: selectedInboxId.value,
      agentList: selectedInboxMemberIds.value,
    });
    useAlert(t('ESSENTIAL.ADMIN.INBOXES.MEMBERS_SUCCESS'));
  } catch (error) {
    useAlert(t('ESSENTIAL.ADMIN.INBOXES.MEMBERS_ERROR'));
  } finally {
    isSavingInboxMembers.value = false;
  }
};

onMounted(async () => {
  await Promise.allSettled([
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
    store.dispatch('inboxes/get'),
  ]);
});

watch(
  teams,
  teamList => {
    if (!selectedTeamId.value && teamList.length) {
      selectTeam(teamList[0].id);
    }
  },
  { immediate: true }
);

watch(
  selectedTeamId,
  async teamId => {
    syncSelectedTeamForm();
    await fetchSelectedTeamMembers(teamId);
    syncSelectedTeamForm();
  },
  { immediate: true }
);

watch(selectedTeamMembers, () => syncSelectedTeamForm());

watch(
  agents,
  agentList => {
    if (!agentList.length) {
      selectVisibilityAgent('');
      return;
    }

    const hasSelectedAgent = agentList.some(
      agent => String(agent.id) === String(selectedVisibilityAgentId.value)
    );
    if (!hasSelectedAgent) {
      selectVisibilityAgent(agentList[0].id);
    }
  },
  { immediate: true }
);

watch(selectedVisibilityAgentId, () => syncVisibilityForm(), {
  immediate: true,
});

watch(selectedVisibilityAgent, () => syncVisibilityForm());

watch(
  inboxes,
  inboxList => {
    if (!inboxList.length) {
      selectInbox('');
      return;
    }

    const hasSelectedInbox = inboxList.some(
      inbox => String(inbox.id) === String(selectedInboxId.value)
    );
    if (!hasSelectedInbox) {
      selectInbox(inboxList[0].id);
    }
  },
  { immediate: true }
);

watch(
  selectedInboxId,
  inboxId => {
    fetchSelectedInboxMembers(inboxId);
  },
  { immediate: true }
);
</script>

<template>
  <section class="flex flex-col w-full h-full min-w-0 bg-n-surface-1">
    <header
      class="flex flex-col gap-4 p-5 border-b border-n-weak lg:flex-row lg:items-center lg:justify-between"
    >
      <div class="flex items-center min-w-0 gap-3">
        <span
          class="flex items-center justify-center flex-shrink-0 rounded-xl size-10 bg-n-teal-9 text-white"
        >
          <i class="i-lucide-layout-dashboard size-5" />
        </span>
        <div class="min-w-0">
          <h1 class="m-0 text-lg font-semibold truncate text-n-slate-12">
            {{ $t('ESSENTIAL.ADMIN.TITLE') }}
          </h1>
          <p class="m-0 text-sm text-n-slate-11">
            {{ $t('ESSENTIAL.ADMIN.SUBTITLE') }}
          </p>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <div
          class="inline-flex max-w-full items-center gap-1.5 rounded-md bg-n-slate-3 px-2 py-1 text-xs font-medium text-n-slate-11"
        >
          <i class="i-lucide-user-round size-3.5 flex-shrink-0" />
          <span class="truncate">{{ currentUserContext }}</span>
        </div>
        <Button
          slate
          faded
          sm
          icon="i-lucide-inbox"
          :label="$t('ESSENTIAL.ADMIN.OPEN_ESSENTIAL')"
          @click="navigateTo('essential')"
        />
        <Button
          sm
          icon="i-lucide-monitor"
          :label="$t('ESSENTIAL.ADMIN.OPEN_PRO')"
          @click="navigateTo('dashboard')"
        />
      </div>
    </header>

    <div class="flex-1 min-h-0 overflow-y-auto p-5">
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <section
          v-for="stat in stats"
          :key="stat.id"
          class="rounded-lg border border-n-weak p-4"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="text-sm font-medium text-n-slate-11">
              {{ stat.label }}
            </span>
            <i class="size-4 text-n-slate-10" :class="[stat.icon]" />
          </div>
          <strong class="mt-2 block text-2xl font-semibold text-n-slate-12">
            {{ stat.value }}
          </strong>
        </section>
      </div>

      <div class="grid gap-4 mt-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <section class="rounded-lg border border-n-weak">
          <div
            class="flex flex-col gap-3 p-4 border-b border-n-weak lg:flex-row lg:items-center lg:justify-between"
          >
            <div>
              <h2 class="m-0 text-base font-semibold text-n-slate-12">
                {{ $t('ESSENTIAL.ADMIN.AGENTS.TITLE') }}
              </h2>
              <p class="m-0 text-sm text-n-slate-11">
                {{ $t('ESSENTIAL.ADMIN.AGENTS.DESC') }}
              </p>
            </div>
            <Button
              slate
              faded
              sm
              icon="i-lucide-external-link"
              :label="$t('ESSENTIAL.ADMIN.ADVANCED')"
              @click="navigateTo('settings/agents/list')"
            />
          </div>

          <div class="divide-y divide-n-weak">
            <div
              v-for="agent in agents"
              :key="agent.id"
              class="grid gap-3 p-4 lg:grid-cols-[minmax(0,1fr)_180px]"
            >
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <span class="font-medium truncate text-n-slate-12">
                    {{ agent.name }}
                  </span>
                  <span
                    v-if="agent.role === 'administrator'"
                    class="rounded bg-n-slate-3 px-1.5 py-0.5 text-[11px] font-medium text-n-slate-11"
                  >
                    {{ $t('ESSENTIAL.ADMIN.AGENTS.ADMIN') }}
                  </span>
                </div>
                <p class="m-0 text-sm truncate text-n-slate-11">
                  {{ agent.email }}
                </p>
              </div>

              <label class="flex flex-col gap-1 mb-0">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AGENT_MGMT.INTERFACE_MODE.LABEL') }}
                </span>
                <select
                  class="w-full !mb-0 text-sm"
                  :value="agent.interface_mode || 'pro'"
                  :disabled="savingAgents[agent.id] || agentUiFlags.isUpdating"
                  @change="updateInterfaceMode(agent, $event.target.value)"
                >
                  <option
                    v-for="mode in interfaceModes"
                    :key="mode.id"
                    :value="mode.id"
                  >
                    {{ mode.label }}
                  </option>
                </select>
              </label>
            </div>

            <div v-if="!agents.length" class="p-6 text-sm text-n-slate-11">
              {{ $t('ESSENTIAL.ADMIN.AGENTS.EMPTY') }}
            </div>
          </div>

          <div class="border-t border-n-weak p-4">
            <div class="flex flex-col gap-1">
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                {{ $t('ESSENTIAL.ADMIN.AGENTS.VISIBILITY_TITLE') }}
              </h3>
              <p class="m-0 text-sm text-n-slate-11">
                {{ $t('ESSENTIAL.ADMIN.AGENTS.VISIBILITY_DESC') }}
              </p>
            </div>

            <div v-if="agents.length" class="mt-3 flex flex-wrap gap-2">
              <button
                v-for="agent in agents"
                :key="agent.id"
                type="button"
                class="rounded-md px-2 py-1 text-xs font-medium"
                :class="
                  String(selectedVisibilityAgentId) === String(agent.id)
                    ? 'bg-n-brand/10 text-n-brand'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
                @click="selectVisibilityAgent(agent.id)"
              >
                {{ agent.name }}
              </button>
            </div>

            <div
              v-if="selectedVisibilityAgentId"
              class="mt-4 flex flex-col gap-3"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="min-w-0">
                  <h4 class="m-0 truncate text-sm font-medium text-n-slate-12">
                    {{ $t('ESSENTIAL.ADMIN.AGENTS.VISIBLE_TEAMS_TITLE') }}
                  </h4>
                  <p class="m-0 truncate text-xs text-n-slate-11">
                    {{ selectedVisibilityAgent.email }}
                  </p>
                </div>
                <span class="flex-shrink-0 text-xs text-n-slate-11">
                  {{
                    selectedVisibleTeamIds.length
                      ? $t('ESSENTIAL.ADMIN.AGENTS.SELECTED_TEAMS_COUNT', {
                          selected: selectedVisibleTeamIds.length,
                          total: teams.length,
                        })
                      : $t('ESSENTIAL.ADMIN.AGENTS.ALL_TEAMS')
                  }}
                </span>
              </div>

              <button
                type="button"
                class="self-start rounded-md px-2 py-1 text-xs font-medium"
                :class="
                  selectedVisibleTeamIds.length === 0
                    ? 'bg-n-brand/10 text-n-brand'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
                @click="showAllTeamsForAgent"
              >
                {{ $t('ESSENTIAL.ADMIN.AGENTS.ALL_TEAMS') }}
              </button>

              <div
                class="flex max-h-40 flex-col gap-2 overflow-y-auto rounded-md border border-n-weak p-2"
              >
                <label
                  v-for="team in teams"
                  :key="team.id"
                  class="flex items-center gap-2 mb-0 cursor-pointer rounded px-2 py-1 hover:bg-n-alpha-2"
                >
                  <input
                    type="checkbox"
                    class="!mb-0"
                    :checked="isTeamVisibleForAgent(team.id)"
                    @change="toggleVisibleTeam(team.id)"
                  />
                  <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
                    {{ team.name }}
                  </span>
                </label>
                <span
                  v-if="!teams.length"
                  class="px-2 py-1 text-sm text-n-slate-11"
                >
                  {{ $t('ESSENTIAL.ADMIN.TEAMS.EMPTY') }}
                </span>
              </div>

              <label class="flex items-center gap-2 mb-0 cursor-pointer">
                <input
                  v-model="selectedFilterAssignedOnly"
                  type="checkbox"
                  class="!mb-0"
                />
                <span class="text-sm text-n-slate-12">
                  {{ $t('ESSENTIAL.ADMIN.AGENTS.FILTER_ASSIGNED') }}
                </span>
              </label>
              <label class="flex items-center gap-2 mb-0 cursor-pointer">
                <input
                  v-model="selectedFilterUnassignedOnly"
                  type="checkbox"
                  class="!mb-0"
                />
                <span class="text-sm text-n-slate-12">
                  {{ $t('ESSENTIAL.ADMIN.AGENTS.FILTER_UNASSIGNED') }}
                </span>
              </label>

              <Button
                sm
                icon="i-lucide-eye"
                :label="$t('ESSENTIAL.ADMIN.AGENTS.SAVE_VISIBILITY')"
                :is-loading="isSavingVisibility || agentUiFlags.isUpdating"
                @click="saveAgentVisibility"
              />
            </div>
          </div>
        </section>

        <div class="flex flex-col gap-4">
          <EssentialChannelHealth :inboxes="inboxes" default-expanded />

          <section class="rounded-lg border border-n-weak p-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <h2 class="m-0 text-base font-semibold text-n-slate-12">
                  {{ $t('ESSENTIAL.ADMIN.TEAMS.TITLE') }}
                </h2>
                <p class="m-0 text-sm text-n-slate-11">
                  {{ $t('ESSENTIAL.ADMIN.TEAMS.DESC') }}
                </p>
              </div>
              <Button
                slate
                faded
                sm
                icon="i-lucide-external-link"
                :label="$t('ESSENTIAL.ADMIN.ADVANCED')"
                @click="navigateTo('settings/teams/list')"
              />
            </div>
            <form class="mt-4 flex flex-col gap-2" @submit.prevent="createTeam">
              <label class="flex flex-col gap-1 mb-0">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('ESSENTIAL.ADMIN.TEAMS.CREATE_LABEL') }}
                </span>
                <input
                  v-model="newTeamName"
                  type="text"
                  class="!mb-0 text-sm"
                  :placeholder="$t('ESSENTIAL.ADMIN.TEAMS.CREATE_PLACEHOLDER')"
                />
              </label>
              <Button
                sm
                type="submit"
                icon="i-lucide-plus"
                :label="$t('ESSENTIAL.ADMIN.TEAMS.CREATE')"
                :is-loading="isSavingTeam"
              />
            </form>

            <div class="mt-4 flex flex-wrap gap-2">
              <button
                v-for="team in teams"
                :key="team.id"
                type="button"
                class="rounded-md px-2 py-1 text-xs font-medium"
                :class="
                  String(selectedTeamId) === String(team.id)
                    ? 'bg-n-brand/10 text-n-brand'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
                @click="selectTeam(team.id)"
              >
                {{ team.name }}
              </button>
              <span v-if="!teams.length" class="text-sm text-n-slate-11">
                {{ $t('ESSENTIAL.ADMIN.TEAMS.EMPTY') }}
              </span>
            </div>

            <div
              v-if="selectedTeamId"
              class="mt-4 flex flex-col gap-3 border-t border-n-weak pt-4"
            >
              <label class="flex flex-col gap-1 mb-0">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('ESSENTIAL.ADMIN.TEAMS.NAME_LABEL') }}
                </span>
                <input
                  v-model="selectedTeamName"
                  type="text"
                  class="!mb-0 text-sm"
                />
              </label>
              <Button
                slate
                faded
                sm
                icon="i-lucide-save"
                :label="$t('ESSENTIAL.ADMIN.TEAMS.SAVE_NAME')"
                :is-loading="isSavingTeam"
                @click="updateTeamName"
              />

              <div class="flex items-center justify-between gap-3">
                <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                  {{ $t('ESSENTIAL.ADMIN.TEAMS.MEMBERS_TITLE') }}
                </h3>
                <span class="text-xs text-n-slate-11">
                  {{
                    $t('ESSENTIAL.ADMIN.TEAMS.SELECTED_COUNT', {
                      selected: selectedTeamMemberIds.length,
                      total: agents.length,
                    })
                  }}
                </span>
              </div>

              <div
                class="flex max-h-48 flex-col gap-2 overflow-y-auto rounded-md border border-n-weak p-2"
              >
                <label
                  v-for="agent in agents"
                  :key="agent.id"
                  class="flex items-center gap-2 mb-0 cursor-pointer rounded px-2 py-1 hover:bg-n-alpha-2"
                >
                  <input
                    type="checkbox"
                    class="!mb-0"
                    :checked="isAgentSelectedForTeam(agent.id)"
                    :disabled="teamMembersUiFlags.isFetching"
                    @change="toggleTeamMember(agent.id)"
                  />
                  <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
                    {{ agent.name }}
                  </span>
                </label>
              </div>

              <Button
                sm
                icon="i-lucide-users-round"
                :label="$t('ESSENTIAL.ADMIN.TEAMS.SAVE_MEMBERS')"
                :is-loading="isSavingMembers || teamMembersUiFlags.isFetching"
                @click="saveTeamMembers"
              />
            </div>
          </section>

          <section class="rounded-lg border border-n-weak p-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <h2 class="m-0 text-base font-semibold text-n-slate-12">
                  {{ $t('ESSENTIAL.ADMIN.INBOXES.TITLE') }}
                </h2>
                <p class="m-0 text-sm text-n-slate-11">
                  {{ $t('ESSENTIAL.ADMIN.INBOXES.DESC') }}
                </p>
              </div>
              <Button
                slate
                faded
                sm
                icon="i-lucide-external-link"
                :label="$t('ESSENTIAL.ADMIN.ADVANCED')"
                @click="navigateTo('settings/inboxes/list')"
              />
            </div>
            <div class="mt-4 flex max-h-48 flex-col gap-2 overflow-y-auto">
              <button
                v-for="inbox in inboxes"
                :key="inbox.id"
                type="button"
                class="flex items-center justify-between gap-3 rounded-md px-2 py-1 text-left text-xs font-medium"
                :class="
                  String(selectedInboxId) === String(inbox.id)
                    ? 'bg-n-brand/10 text-n-brand'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
                @click="selectInbox(inbox.id)"
              >
                <span class="flex min-w-0 items-center gap-2">
                  <i
                    class="size-3.5 flex-shrink-0"
                    :class="[channelIconForInbox(inbox)]"
                  />
                  <span class="truncate">{{ inbox.name }}</span>
                </span>
                <span class="flex-shrink-0">{{ channelLabel(inbox) }}</span>
              </button>
              <span v-if="!inboxes.length" class="text-sm text-n-slate-11">
                {{ $t('ESSENTIAL.ADMIN.INBOXES.EMPTY') }}
              </span>
            </div>

            <div
              v-if="selectedInboxId"
              class="mt-4 flex flex-col gap-3 border-t border-n-weak pt-4"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="min-w-0">
                  <h3
                    class="m-0 truncate text-sm font-semibold text-n-slate-12"
                  >
                    {{ $t('ESSENTIAL.ADMIN.INBOXES.MEMBERS_TITLE') }}
                  </h3>
                  <p class="m-0 truncate text-xs text-n-slate-11">
                    {{ selectedInbox.name }}
                  </p>
                </div>
                <span class="flex-shrink-0 text-xs text-n-slate-11">
                  {{
                    $t('ESSENTIAL.ADMIN.INBOXES.SELECTED_COUNT', {
                      selected: selectedInboxMemberIds.length,
                      total: agents.length,
                    })
                  }}
                </span>
              </div>

              <div
                v-if="isFetchingInboxMembers"
                class="rounded-md border border-n-weak p-3 text-sm text-n-slate-11"
              >
                {{ $t('ESSENTIAL.ADMIN.INBOXES.LOADING_MEMBERS') }}
              </div>
              <div
                v-else
                class="flex max-h-48 flex-col gap-2 overflow-y-auto rounded-md border border-n-weak p-2"
              >
                <label
                  v-for="agent in agents"
                  :key="agent.id"
                  class="flex items-center gap-2 mb-0 cursor-pointer rounded px-2 py-1 hover:bg-n-alpha-2"
                >
                  <input
                    type="checkbox"
                    class="!mb-0"
                    :checked="isAgentSelectedForInbox(agent.id)"
                    @change="toggleInboxMember(agent.id)"
                  />
                  <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
                    {{ agent.name }}
                  </span>
                </label>
              </div>

              <Button
                sm
                icon="i-lucide-radio-receiver"
                :label="$t('ESSENTIAL.ADMIN.INBOXES.SAVE_MEMBERS')"
                :is-loading="isSavingInboxMembers || isFetchingInboxMembers"
                @click="saveInboxMembers"
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  </section>
</template>
